import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Download, Filter, X, Bookmark, BookmarkPlus } from "lucide-react";
import { STATUS_META, STATUS_OPTIONS, GENDER_OPTIONS, ENGLISH_LEVELS, ENGLISH_LEVEL_CHIP } from "@/lib/status";
import { toast } from "sonner";

const ALL = "__all__";

export default function CandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    job_category: searchParams.get("job_category") || "",
    industry: searchParams.get("industry") || "",
    gender: searchParams.get("gender") || "",
    location: searchParams.get("location") || "",
    area: searchParams.get("area") || "",
    english_level: searchParams.get("english_level") || "",
    status: searchParams.get("status") || "",
    min_age: searchParams.get("min_age") || "",
    max_age: searchParams.get("max_age") || "",
    min_salary: searchParams.get("min_salary") || "",
    max_salary: searchParams.get("max_salary") || "",
    paid: searchParams.get("paid") || "",
  });
  const [mine, setMine] = useState(searchParams.get("mine") === "1");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [citiesWithAreas, setCitiesWithAreas] = useState([]);
  const [areasForCity, setAreasForCity] = useState([]);
  const [presets, setPresets] = useState([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);

  const loadPresets = () =>
    api
      .get("/filter-presets")
      .then((r) => setPresets(r.data.items || []))
      .catch(() => {});

  useEffect(() => {
    loadPresets();
  }, []);

  const savePreset = async () => {
    if (savingPreset) return;
    setSavingPreset(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params[k] = v;
    });
    if (mine) params.mine = "1";
    try {
      await api.post("/filter-presets", { name: presetName, params });
      toast.success(`Preset "${presetName.trim()}" saved`);
      setPresetName("");
      setShowSavePreset(false);
      loadPresets();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPreset(false);
    }
  };

  const applyPreset = (p) => {
    setSearchParams(p.params || {}, { replace: true });
  };

  const deletePreset = async (p) => {
    try {
      await api.delete(`/filter-presets/${p.id}`);
      setPresets((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Preset deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    api.get("/meta/job-categories").then((r) => setCategories(r.data));
    api.get("/meta/industries").then((r) => setIndustries(r.data));
    api
      .get("/meta/areas")
      .then((r) => setCitiesWithAreas(r.data.cities_with_areas || []));
  }, []);

  // Fetch areas whenever location filter changes to a known city
  useEffect(() => {
    const city = filters.location;
    if (!city || !citiesWithAreas.some((c) => c.toLowerCase() === city.toLowerCase())) {
      setAreasForCity([]);
      return;
    }
    api
      .get("/meta/areas", { params: { city } })
      .then((r) => setAreasForCity(r.data.areas || []))
      .catch(() => setAreasForCity([]));
  }, [filters.location, citiesWithAreas]);

  const loadSeq = useRef(0);

  const load = async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params[k] = v;
    });
    if (mine) params.mine = true;
    try {
      const { data } = await api.get("/candidates", { params });
      if (seq !== loadSeq.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      if (seq === loadSeq.current) toast.error(formatApiError(e));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  // Auto-reload when URL params change (e.g., user clicked a dashboard tile or pressed Back)
  useEffect(() => {
    const next = {
      search: searchParams.get("search") || "",
      job_category: searchParams.get("job_category") || "",
      industry: searchParams.get("industry") || "",
      gender: searchParams.get("gender") || "",
      location: searchParams.get("location") || "",
      area: searchParams.get("area") || "",
      english_level: searchParams.get("english_level") || "",
      status: searchParams.get("status") || "",
      min_age: searchParams.get("min_age") || "",
      max_age: searchParams.get("max_age") || "",
      min_salary: searchParams.get("min_salary") || "",
      max_salary: searchParams.get("max_salary") || "",
      paid: searchParams.get("paid") || "",
    };
    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
    setMine(searchParams.get("mine") === "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, filters]);

  // Keep URL in sync with filters so browser Back returns to the same filtered view
  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params[k] = v;
    });
    if (mine) params.mine = "1";
    const current = Object.fromEntries(searchParams.entries());
    const same =
      Object.keys(params).length === Object.keys(current).length &&
      Object.keys(params).every((k) => current[k] === String(params[k]));
    if (!same) setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, mine]);

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
    setFilters({
      search: "",
      job_category: "",
      industry: "",
      gender: "",
      location: "",
      area: "",
      english_level: "",
      status: "",
      min_age: "",
      max_age: "",
      min_salary: "",
      max_salary: "",
      paid: "",
    });
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/candidates/export/excel", {
        responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tulsi-candidates-${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AppShell
      title="Candidates"
      subtitle={`${total} candidate${total !== 1 ? "s" : ""} found`}
      actions={
        <>
          <Button
            data-testid="toggle-mine-btn"
            onClick={() => setMine((m) => !m)}
            variant={mine ? "default" : "outline"}
            className={
              mine
                ? "bg-brand-700 hover:bg-brand-800 text-white"
                : "border-brand-200 text-brand-700 hover:bg-brand-50"
            }
          >
            {mine ? "Showing: Mine" : "Show only mine"}
          </Button>
          <Button
            data-testid="export-excel-btn"
            onClick={exportExcel}
            variant="outline"
            className="border-brand-200 text-brand-700 hover:bg-brand-50"
          >
            <Download size={16} className="mr-2" />
            Export Excel
          </Button>
        </>
      }
    >
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Filter rail */}
        <Card className="tj-card lg:sticky lg:top-6 self-start" data-testid="filter-panel">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-brand-700" />
                <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-ink-800">
                  Filters
                </h3>
              </div>
              <button
                data-testid="clear-filters-btn"
                onClick={clearFilters}
                className="text-xs text-ink-600 hover:text-brand-700 inline-flex items-center gap-1"
              >
                <X size={12} /> Clear
              </button>
            </div>

            <div className="space-y-2" data-testid="presets-section">
              <div className="flex items-center justify-between">
                <Label className="text-xs inline-flex items-center gap-1.5">
                  <Bookmark size={12} className="text-brand-700" /> Saved Presets
                </Label>
                <button
                  type="button"
                  data-testid="preset-save-toggle"
                  onClick={() => setShowSavePreset((v) => !v)}
                  className="text-[11px] text-brand-700 hover:text-brand-800 font-medium inline-flex items-center gap-1"
                >
                  <BookmarkPlus size={12} /> Save current
                </button>
              </div>
              {showSavePreset && (
                <div className="flex gap-1.5">
                  <Input
                    data-testid="preset-name-input"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="e.g. Kolkata Telecallers"
                    className="h-8 text-xs"
                    maxLength={60}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && presetName.trim()) savePreset();
                    }}
                  />
                  <Button
                    data-testid="preset-save-btn"
                    size="sm"
                    onClick={savePreset}
                    disabled={!presetName.trim() || savingPreset}
                    className="h-8 px-3 bg-brand-700 hover:bg-brand-800 text-white text-xs"
                  >
                    Save
                  </Button>
                </div>
              )}
              {presets.length === 0 ? (
                !showSavePreset && (
                  <p className="text-[11px] text-ink-500">
                    Set filters, then save them for one-click reuse.
                  </p>
                )
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p) => (
                    <span
                      key={p.id}
                      className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50 pl-2.5 pr-1 py-0.5"
                    >
                      <button
                        type="button"
                        data-testid={`preset-chip-${p.id}`}
                        onClick={() => applyPreset(p)}
                        title={Object.entries(p.params || {})
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(", ")}
                        className="text-[11px] font-medium text-brand-800 hover:text-brand-900 max-w-[160px] truncate"
                      >
                        {p.name}
                      </button>
                      <button
                        type="button"
                        data-testid={`preset-delete-${p.id}`}
                        onClick={() => deletePreset(p)}
                        className="h-4 w-4 grid place-items-center rounded-full text-brand-700 hover:bg-brand-200"
                        aria-label={`Delete preset ${p.name}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Name / Phone / Email</Label>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <Input
                  data-testid="filter-search"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  placeholder="Search…"
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Job Category</Label>
              <Select
                value={filters.job_category || ALL}
                onValueChange={(v) =>
                  setFilters({ ...filters, job_category: v === ALL ? "" : v })
                }
              >
                <SelectTrigger data-testid="filter-category" className="h-9 text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All Categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Industry</Label>
              <Select
                value={filters.industry || ALL}
                onValueChange={(v) =>
                  setFilters({ ...filters, industry: v === ALL ? "" : v })
                }
              >
                <SelectTrigger data-testid="filter-industry" className="h-9 text-sm">
                  <SelectValue placeholder="Any industry" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value={ALL}>Any industry</SelectItem>
                  {industries.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Gender</Label>
              <Select
                value={filters.gender || ALL}
                onValueChange={(v) =>
                  setFilters({ ...filters, gender: v === ALL ? "" : v })
                }
              >
                <SelectTrigger data-testid="filter-gender" className="h-9 text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any</SelectItem>
                  {GENDER_OPTIONS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Location (city)</Label>
              <Select
                value={filters.location || ALL}
                onValueChange={(v) =>
                  setFilters({
                    ...filters,
                    location: v === ALL ? "" : v,
                    area: "",
                  })
                }
              >
                <SelectTrigger data-testid="filter-location" className="h-9 text-sm">
                  <SelectValue placeholder="Any city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any city</SelectItem>
                  {citiesWithAreas.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {areasForCity.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Area in {filters.location}
                </Label>
                <Select
                  value={filters.area || ALL}
                  onValueChange={(v) =>
                    setFilters({ ...filters, area: v === ALL ? "" : v })
                  }
                >
                  <SelectTrigger data-testid="filter-area" className="h-9 text-sm">
                    <SelectValue placeholder="Any area" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={ALL}>Any area</SelectItem>
                    {areasForCity.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">English Level</Label>
              <Select
                value={filters.english_level || ALL}
                onValueChange={(v) =>
                  setFilters({ ...filters, english_level: v === ALL ? "" : v })
                }
              >
                <SelectTrigger data-testid="filter-english" className="h-9 text-sm">
                  <SelectValue placeholder="Any level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any level</SelectItem>
                  {ENGLISH_LEVELS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select
                value={filters.status || ALL}
                onValueChange={(v) =>
                  setFilters({ ...filters, status: v === ALL ? "" : v })
                }
              >
                <SelectTrigger data-testid="filter-status" className="h-9 text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min Age</Label>
                <Input
                  data-testid="filter-min-age"
                  type="number"
                  value={filters.min_age}
                  onChange={(e) =>
                    setFilters({ ...filters, min_age: e.target.value })
                  }
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max Age</Label>
                <Input
                  data-testid="filter-max-age"
                  type="number"
                  value={filters.max_age}
                  onChange={(e) =>
                    setFilters({ ...filters, max_age: e.target.value })
                  }
                  className="h-9 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Min Salary (₹/month)</Label>
                <Input
                  data-testid="filter-min-salary"
                  type="number"
                  step="500"
                  value={filters.min_salary}
                  onChange={(e) =>
                    setFilters({ ...filters, min_salary: e.target.value })
                  }
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max Salary</Label>
                <Input
                  data-testid="filter-max-salary"
                  type="number"
                  step="500"
                  value={filters.max_salary}
                  onChange={(e) =>
                    setFilters({ ...filters, max_salary: e.target.value })
                  }
                  className="h-9 text-sm"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="filter-salary-chips">
              {[
                { label: "< ₹10K", min: "", max: "10000" },
                { label: "₹10–15K", min: "10000", max: "15000" },
                { label: "₹15–25K", min: "15000", max: "25000" },
                { label: "₹25–40K", min: "25000", max: "40000" },
                { label: "₹40K+", min: "40000", max: "" },
              ].map((c) => {
                const active =
                  filters.min_salary === c.min && filters.max_salary === c.max;
                return (
                  <button
                    type="button"
                    key={c.label}
                    data-testid={`filter-salary-chip-${c.label}`}
                    onClick={() =>
                      setFilters({
                        ...filters,
                        min_salary: c.min,
                        max_salary: c.max,
                      })
                    }
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      active
                        ? "bg-brand-700 text-white border-brand-700"
                        : "bg-white text-ink-700 border-slate-300 hover:border-brand-400 hover:bg-brand-50"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>

            <Button
              data-testid="apply-filters-btn"
              onClick={load}
              className="w-full bg-brand-700 hover:bg-brand-800 text-white"
            >
              Apply Filters
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="space-y-3" data-testid="candidates-list">
          {loading ? (
            <div className="text-sm text-ink-600 animate-pulse">Loading candidates…</div>
          ) : items.length === 0 ? (
            <Card className="tj-card">
              <CardContent className="p-10 text-center text-ink-600">
                <p className="font-medium text-ink-800 mb-1">No candidates yet</p>
                <p className="text-sm">
                  Share the public form link to start collecting applications.
                </p>
                <Link
                  to="/share"
                  className="inline-block mt-4 text-sm text-brand-700 hover:underline font-medium"
                >
                  → Get share link
                </Link>
              </CardContent>
            </Card>
          ) : (
            items.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.new;
              return (
                <Link
                  key={c.id}
                  to={`/candidates/${c.id}`}
                  data-testid={`candidate-row-${c.id}`}
                  className="block"
                >
                  <Card className="tj-card tj-card-hover">
                    <CardContent className="p-4 md:p-5">
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 shrink-0 rounded-full bg-brand-100 text-brand-800 grid place-items-center font-heading font-semibold text-lg">
                          {(c.full_name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-heading font-semibold text-ink-900 truncate">
                              {c.full_name}
                            </h3>
                            <Badge className={`${meta.cls} border-0`}>
                              {meta.label}
                            </Badge>
                            {c.lined_up_for && (
                              <Badge className="bg-orange-50 text-orange-800 border-0">
                                → {c.lined_up_for}
                              </Badge>
                            )}
                            {c.resume_download_paid && (
                              <Badge
                                data-testid={`paid-badge-${c.id}`}
                                className="bg-emerald-100 text-emerald-800 border-0"
                              >
                                Paid ₹200
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-ink-600">
                            {c.job_category}{" "}
                            {c.current_location && (
                              <span>
                                · {c.current_location}
                                {c.current_area ? ` (${c.current_area})` : ""}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-600">
                            <span>📱 {c.phone}</span>
                            {c.age != null && <span>{c.age} yrs</span>}
                            {c.expected_salary != null && (
                              <span>Exp. ₹{Number(c.expected_salary).toLocaleString("en-IN")}/mo</span>
                            )}
                            {c.total_experience != null && (
                              <span>{c.total_experience} yr exp.</span>
                            )}
                            {c.has_cv && <span className="text-brand-700">CV attached</span>}
                            {c.english_level && (
                              <span
                                className={`tj-pill ${
                                  ENGLISH_LEVEL_CHIP[c.english_level] ||
                                  "bg-slate-100 text-ink-700"
                                }`}
                              >
                                English: {c.english_level}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </AppShell>
  );
}
